/**
* 常用工具方法封装
* 提供全局对象 Utils/tools （可以自定义命名空间 ）, 避免和其他框架工具冲突 
* 在浏览器环境中 global 参数会被设置为 window 对象，因此 global.Utils = factory() 实际上等同于 window.Utils = factory()
*/
(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
    typeof define === 'function' && define.amd ? define(factory) :
      (global.tools = factory());
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /**
  * 判断值是否为空
  * @param {*} val - 要判断的值
  * @returns {boolean} 是否为空
  */
  function isNull(val) {
    // null 和 undefined 检查
    if (val === null || val === undefined) {
      return true;
    }
    // 字符串检查（包括空白字符串————" "、"\t\n"都会移除）
    if (typeof val === 'string') {
      return val.trim().length === 0;
    }
    // 数字检查（NaN被认为为空）
    if (typeof val === 'number') {
      return isNaN(val);
    }
    // Map 和 Set 检查
    if (val instanceof Map || val instanceof Set) {
      return val.size === 0;
    }
    // 数组检查
    if (Array.isArray(val)) {
      return val.length === 0;
    }
    // 对象检查
    if (val instanceof Object && val.constructor === Object) {
      return Object.keys(val).length === 0;
    }
    // 其他类型默认不为空
    return false;
  }

  /**
 * 检查是否为数组
 * @param {*} value - 要检查的值
 * @returns {boolean} 是否为数组
 */
  function isArray(value) {
    return Array.isArray(value);
  }

  /**
   * 检查是否为对象
   * @param {*} value - 要检查的值
   * @returns {boolean} 是否为对象
   */
  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  /**
   * 检查是否为函数
   * @param {*} value - 要检查的值
   * @returns {boolean} 是否为函数
   */
  function isFunction(value) {
    return typeof value === 'function';
  }

  /**
   * 检查是否为字符串
   * @param {*} value - 要检查的值
   * @returns {boolean} 是否为字符串
   */
  function isString(value) {
    return typeof value === 'string';
  }

  /**
   * 检查是否为数字
   * @param {*} value - 要检查的值
   * isNaN是window对象上的一个方法，用于判断一个值是否为NaN。
   * @returns {boolean} 是否为数字
   */
  function isNumber(value) {
    return typeof value === 'number' && !isNaN(value);
  }

  /**
  * 验证URL格式
  * @param {string} url - URL地址
  * @returns {boolean} 是否为有效URL
  */
  function isUrl(url) {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
  * 判断运行环境是否为微信内置浏览器
  * @returns {boolean} 是否为微信内置浏览器打开
  */
  function isWeixin() {
    const ua = navigator.userAgent.toLowerCase();
    return ua.match(/MicroMessenger/i) === "micromessenger";
  }

  /**
  * 验证统一社会信用代码
  * @param {string} code - 统一社会信用代码
  * @returns {boolean} 是否为有效的统一社会信用代码
  */
  function validCreditCode(code) {
    if (!isString(code)) return false;
    const reg = /^[^_IOZSVa-z\W]{2}\d{6}[^_IOZSVa-z\W]{10}$/;
    return reg.test(code.trim());
  }
  /**
   * 密码格式效验 8-16位 字母数字特殊字符组合
   * @param {string} password
   * @returns {Boolean}
   */
  function validPassWord(password) {
    if (!isString(password)) return false;
    const reg = /^(?=.*[0-9].*)(?=.*[A-Z].*)(?=.*[a-z].*)(?=.*[`~!@#$%^&*()_\-+=<>.?:"{}].*).{8,16}$/;
    return reg.test(password);
  }

  /**
  * 验证邮箱格式
  * @param {string} email - 邮箱地址
  * @returns {boolean} 是否为有效邮箱
  */
  function validEmail(email) {
    if (!isString(email)) return false;
    const reg = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return reg.test(email);
  }

  /**
   * 检查是否为有效的电话号码
   * 支持固定电话和手机号
   * @param {*} value - 要检查的值
   * @param {Object} options - 验证选项
   * @param {boolean} options.mobileOnly - 是否只验证手机号
   * @param {boolean} options.landlineOnly - 是否只验证固话
   * @returns {boolean} 是否为有效电话号码
   */
  function validPhone(value, options = {}) {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return false;
    }

    const strValue = String(value);
    const cleanValue = strValue.replace(/[\s\-\(\)]/g, '');

    // 手机号验证
    const mobileRegex = /^1[3-9]\d{9}$/;

    // 固定电话验证（支持区号）
    const landlineRegex = /^(0\d{2,3})?\d{7,8}$/;

    if (options.mobileOnly) {
      return mobileRegex.test(cleanValue);
    }

    if (options.landlineOnly) {
      return landlineRegex.test(cleanValue);
    }

    // 默认支持手机号和固话
    return mobileRegex.test(cleanValue) || landlineRegex.test(cleanValue);
  }

  /**
   * 检查是否为有效的手机号码
   * 支持中国大陆手机号格式
   * @param {*} value - 要检查的值
   * @returns {boolean} 是否为有效手机号
   */
  function validMobile(value) {
    // 基本类型检查
    if (typeof value !== 'string' && typeof value !== 'number') {
      return false;
    }

    // 转换为字符串进行验证
    const strValue = String(value);

    // 去除可能的空格和特殊字符
    const cleanValue = strValue.replace(/[\s\-\(\)]/g, '');

    // 中国大陆手机号正则表达式
    // 支持以1开头的11位数字，第二位为3-9
    const mobileRegex = /^1[3-9]\d{9}$/;

    return mobileRegex.test(cleanValue);
  }

  /**
   * 证件号码校验
   * @param {string} cardType - 证件类型
   * @param {string} cardNo - 证件号码（默认01是身份证）
   * @returns {boolean} 是否符合校验规则
   */
  function validCardNo(cardType = '01', cardNo) {
    if (!isString(cardType) || !isString(cardNo)) return false;

    // 非身份证的验证
    const reg = /^[a-zA-Z0-9]{4,20}$/;

    // 身份证校验
    if (cardType === "01") {
      const city = {
        11: "北京", 12: "天津", 13: "河北", 14: "山西", 15: "内蒙古",
        21: "辽宁", 22: "吉林", 23: "黑龙江", 31: "上海", 32: "江苏",
        33: "浙江", 34: "安徽", 35: "福建", 36: "江西", 37: "山东",
        41: "河南", 42: "湖北", 43: "湖南", 44: "广东", 45: "广西",
        46: "海南", 50: "重庆", 51: "四川", 52: "贵州", 53: "云南",
        54: "西藏", 61: "陕西", 62: "甘肃", 63: "青海", 64: "宁夏",
        65: "新疆", 71: "台湾", 81: "香港", 82: "澳门", 91: "国外"
      };

      if (cardNo.length !== 18 && cardNo.length !== 15) {
        return false;
      }

      const basicReg = /^\d{6}(18|19|20)?\d{2}(0[1-9]|1[012])(0[1-9]|[12]\d|3[01])\d{3}(\d|[Xx])$/;
      if (!cardNo || !basicReg.test(cardNo)) {
        return false;
      }

      if (!city[cardNo.substr(0, 2)]) {
        return false;
      }

      // 18位身份证校验码验证
      if (cardNo.length === 18) {
        const factor = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
        const parity = [1, 0, 'X', 9, 8, 7, 6, 5, 4, 3, 2];
        let sum = 0;

        for (let i = 0; i < 17; i++) {
          sum += parseInt(cardNo[i]) * factor[i];
        }

        const mod = sum % 11;
        return parity[mod].toString().toUpperCase() === cardNo[17].toUpperCase();
      }
      return true;
    } else {
      // 其他证件类型简单校验
      return reg.test(cardNo);
    }
  }



  /**
   * 判断传入的值能否被解析为有效日期
   * @param {*} value - 要判断的值
   * new Date(1000)是第一秒(小于10位或者13位并不报错) 【1970年1月1 00:00:00 +8时区】
   * @returns {boolean} 是否能被解析为有效日期
   */
  function validDate(value) {
    // null 或 undefined 直接返回 false
    if (value === null || value === undefined) {
      return false;
    }

    // Date 对象实例检查
    if (value instanceof Date) {
      return !isNaN(value.getTime());
    }

    // 添加更严格的最小日期检查
    const minDate = new Date(-8640000000000000); // JavaScript支持的最小日期
    const maxDate = new Date(8640000000000000);   // JavaScript支持的最大日期
    if (typeof value === 'number') {
      if (isNaN(value) || value < minDate.getTime() || value > maxDate.getTime()) {
        return false;
      }
      const date = new Date(value);
      return !isNaN(date.getTime());
    }

    // 字符串类型
    if (typeof value === 'string') {
      // 空字符串返回 false
      if (value.trim() === '') {
        return false;
      }

      // 尝试转换为数字（时间戳）
      const num = Number(value);
      // 这个数字代表的是 JavaScript 中 Date 对象能表示的最大毫秒数 275760年
      if (!isNaN(num) && num >= 0 && num <= 8640000000000000) {
        const date = new Date(num);
        return !isNaN(date.getTime());
      }

      // 尝试解析日期字符串
      const date = new Date(value);
      return !isNaN(date.getTime());
    }

    // 其他类型尝试转换为字符串再解析
    try {
      const strValue = String(value);
      if (strValue === '' || strValue === 'null' || strValue === 'undefined') {
        return false;
      }

      const date = new Date(strValue);
      return !isNaN(date.getTime());
    } catch (e) {
      return false;
    }
  }

  /**
  * 数组去重
  * @param {Array} arr - 源数组
  * @returns {Array} 去重后的数组
  */
  function uniqueArray(arr) {
    return [...new Set(arr)];
  }

  /**
  * 深度克隆对象
  * @param {*} source - 源对象
  * @returns {*} 克隆后的对象
  */
  function deepClone(source) {
    // 基础类型直接返回
    if (!source || typeof source !== 'object') {
      return source;
    }

    // 处理特殊对象类型
    if (source instanceof Date) return new Date(source);
    if (source instanceof RegExp) return new RegExp(source);
    if (source instanceof Map) {
      const result = new Map();
      source.forEach((value, key) => {
        result.set(deepClone(key), deepClone(value));
      });
      return result;
    }
    if (source instanceof Set) {
      const result = new Set();
      source.forEach(value => {
        result.add(deepClone(value));
      });
      return result;
    }

    // 处理普通对象和数组
    const targetObj = Array.isArray(source) ? [] : {};

    // 复制所有可枚举属性（包括Symbol键名）
    Reflect.ownKeys(source).forEach(key => {
      targetObj[key] = deepClone(source[key]);
    });

    return targetObj;
  }

  /**
  * 复制文本到剪贴板
  * @param {string} text - 要复制的文本
  * @returns {Promise<boolean>} 是否复制成功, 会返回Promise进行各个端的后续处理（弹框提示等）
  */
  function copyText(text) {
    if (!text) return Promise.resolve(false);

    if (navigator.clipboard && window.isSecureContext) {
      // 使用现代API
      return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
    } else {
      // 降级方案
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const success = document.execCommand('copy');
        textArea.remove();
        return Promise.resolve(success);
      } catch (error) {
        console.warn('Failed to copy text to clipboard:', error);
        return Promise.resolve(false);
      }
    }
  }

  /**
  * 延迟执行
  * @param {number} ms - 延迟毫秒数
  * @returns {Promise} Promise对象
  */
  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
  * 数据脱敏处理
  * @param {string} str - 需要脱敏的字符串
  * @param {string} type - 脱敏类型————[mobile, idCard, name, email, bankCard, address]
  * 默认保留收尾（2位时保留后面）————效果同name
  * @param {Object} options - 脱敏配置选项 { start:1, end:-1, mask:'*' }
  * @returns {string} 脱敏后的字符串
  */
  function maskedStr(str, type = null, options = {}) {
    // 输入验证（null/undefined/数字等, 如需脱敏请传入字符串格式）
    if (!str || typeof str !== 'string') {
      return str;
    }

    // 特殊处理邮箱类型——————email必须显示指明类型是email
    if (type === 'email' && str.includes('@')) {
      const [localPart, domainPart] = str.split('@');
      if (localPart.length > 1) {
        // 对邮箱前缀部分进行脱敏
        const prefix = localPart.substring(0, 1);
        const suffix = localPart.substring(localPart.length - 1);
        const mask = '*'.repeat(localPart.length - 2);
        return `${prefix}${mask}${suffix}@${domainPart}`;
      }
      return str;
    }

    const len = str.length;

    // 默认配置————maskedStr("type传空",'',{mask: '#'})
    // 默认自定义类型脱敏首尾（处理同name一致）
    const defaultOptions = {
      start: len <= 2 ? 0 : 1,
      end: -1,
      mask: '*',
    };

    const presets = {
      // 手机号脱敏
      // 【支付宝、微信、银行APP等广泛使用】 13812345678 => 138****5678
      mobile: { start: 3, end: -4 },

      // 身份证脱敏
      // 【标准做法，隐藏中间8位】 110101199001011234 => 110101********1234
      idCard: { start: 6, end: -4 },

      // 姓名脱敏
      // 1位：不脱敏（如"华" => "华"）
      // 2位：第一个字符脱敏（如"张三" => "*三"）
      // 3位及以上：首尾不脱敏，中间脱敏（如"张小明" => "张*明"，"欧阳小明" => "欧**明"）
      name: {
        start: len <= 2 ? 0 : 1,
        end: -1,
      },

      // 银行卡脱敏
      // 【保留前6位BIN和后4位尾号】 6222021234567890 => 622202******7890
      bankCard: { start: 6, end: -4 },
    };

    // 应用预设配置
    let config = { ...defaultOptions };
    if (presets[type]) {
      config = { ...config, ...presets[type] };
    }
    config = { ...config, ...options };

    // 处理函数形式的配置参数--比如name的动态start
    const getActualValue = (value, str) => {
      return typeof value === 'function' ? value(str) : value;
    };

    // 计算实际的开始和结束位置
    const start = getActualValue(config.start, str);
    const end = getActualValue(config.end, str);

    let startPos = start >= 0 ? start : Math.max(0, str.length + start);
    let endPos = end >= 0 ? end : Math.max(0, str.length + end);

    // 确保位置不超出字符串范围
    startPos = Math.min(startPos, str.length);
    endPos = Math.min(endPos, str.length);

    // 确保 startPos <= endPos
    if (startPos > endPos) {
      [startPos, endPos] = [endPos, startPos];
    }

    // 计算遮罩长度（根据开始和结束位置自动计算）
    const maskLength = Math.max(0, endPos - startPos);

    // 构造脱敏后的字符串
    const prefix = str.substring(0, startPos);
    const mask = config.mask.repeat(maskLength);
    const suffix = str.substring(endPos);

    return prefix + mask + suffix;
  }

  /**
  * 生成一个 start 到 end 的随机数（包含start和end）
  * @param {number} start - 起始值
  * @param {number} end - 结束值
  * @returns {number} 随机数
  */
  function getRandom(start = 0, end = 100) {
    return Math.floor(Math.random() * (end - start + 1)) + start;
  }

  /**
  * 生成唯一ID 36位
  * @returns {string} 唯一ID
  */
  function getUuid() {
    try {
      // 优先使用加密安全的随机数生成器
      if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          const r = crypto.getRandomValues(new Uint8Array(1))[0] % 16;
          const v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      }

      // 回退到 Math.random()
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    } catch (error) {
      console.warn('Failed to generate UUID:', error);
      return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    }
  }

  /**
  * 获取年份
  * @param {string} plus - 偏移量（去年。今年，明年）
  * @returns {string|number} 根据偏移量返回年份
  */
  function getYearCurr(plus = 0) {
    return new Date().getFullYear() + plus;
  }

  /**
  * 获取连续年份数组
  * @param {string} length - 连续几年
  * @param {string} plus - 偏移量
  * @param {string} step - 间隔
  * @returns {string|number} 根据偏移量返回连续年份数组
  */
  function getYearRange(length, plus = 0, step = 1) {
    const start = new Date().getFullYear() + plus
    return Array.from({ length }, (_, i) => start + i*step)
  }
  /**
  * 时间格式化
  * @param {number|string|Date} timestamp - 时间戳、日期字符串或Date对象
  * 时间戳同时支持10位—— 1640995200 和13位—— 1640995200000
  * @param {string} format - 格式字符串，支持自定义格式和预设格式
  * @param {string} defaultValue - 空值时的默认文本
  * @returns {string} 格式化后的时间
  * IOS进行new Date(yyyy-MM-dd)时存在兼容性问题
  */
  function getDatePicker(timestamp, format = 'yyyy/MM/dd', defaultValue = '--') {
    // 空值处理————处理后端返回null的显示
    if (timestamp == null || timestamp == '' || timestamp == undefined) {
      return defaultValue;
    }

    // 支持多种输入类型
    let date;
    if (timestamp instanceof Date) {
      // 处理 Date 对象
      // date = timestamp; // 不推荐, 只是引用, 不是实例
      date = new Date(timestamp.getTime());
    } else {
      // 处理其他类型（字符串、数字等————时间戳）
      let parsedTs;

      // 更精确地判断时间戳单位
      if (typeof timestamp === 'number') {
        // 一般认为 1970-01-01 00:00:00 到 2038-01-19 03:14:07 之间的秒级时间戳是有效的
        if (timestamp >= 0 && timestamp <= 2147483647 && Math.floor(timestamp) === timestamp) {
          parsedTs = timestamp * 1000;
        } else {
          parsedTs = timestamp;
        }
      } else if (typeof timestamp === 'string') {
        // 如果可以转换为数字且为整数，按照数字逻辑处理
        if (!isNaN(asNumber) && timestamp.trim() !== '' && /^\d+$/.test(timestamp.trim())) {
          if (asNumber >= 0 && asNumber <= 2147483647) {
            parsedTs = asNumber * 1000;
          } else {
            parsedTs = asNumber;
          }
        } else {
          parsedTs = timestamp;
        }
      } else {
        parsedTs = timestamp;
      }
      // 非时间格式new Date() 不会报错, 只会时间不正确或者Invalid Date
      date = new Date(parsedTs);
    }

    // 日期有效性检查
    if (date.toString() === 'Invalid Date' || isNaN(date.getTime())) {
      return noneText;
    }

    // 预设格式映射
    const presetFormats = {
      // 基础日期格式
      'date': 'yyyy/MM/dd',                 // 2022/01/01
      'date-dash': 'yyyy-MM-dd',            // 2022-01-01
      'date-cn': 'yyyy年MM月dd日',           // 2022年01月01日

      // 完整时间格式
      'datetime': 'yyyy/MM/dd hh:mm:ss',        // 2022/01/01 08:00:00
      'datetime-dash': 'yyyy-MM-dd hh:mm:ss',   // 2022-01-01 08:00:00
      'datetime-cn': 'yyyy年MM月dd日 hh:mm:ss',  // 2022年01月01日 08:00:00

      // 不含秒的时间格式
      'minute': 'yyyy/MM/dd hh:mm',       // 2022/01/01 08:00
      'minute-dash': 'yyyy-MM-dd hh:mm',  // 2022-01-01 08:00
      'minute-cn': 'yyyy年MM月dd日 hh:mm',// 2022年01月01日 08:00

      // 仅时间格式
      'time': 'hh:mm:ss',                   // 08:00:00
      'time-short': 'hh:mm',                // 08:00

      // 月份和年份
      'month': 'yyyy/MM',                   // 2022/01
      'year': 'yyyy',                       // 2022
    };

    // 如果是预设格式，转换为实际格式字符串
    const actualFormat = presetFormats[format] || format;

    // 格式化组件
    const formatParts = {
      'yyyy': date.getFullYear(),
      'MM': String(date.getMonth() + 1).padStart(2, '0'),
      'dd': String(date.getDate()).padStart(2, '0'),
      'hh': String(date.getHours()).padStart(2, '0'),
      'mm': String(date.getMinutes()).padStart(2, '0'),
      'ss': String(date.getSeconds()).padStart(2, '0')
    };

    // 替换格式字符串中的占位符（自定义格式）
    // （年份:yyyy_月份MM_自然日dd ——————会替换yyyyMMdd等匹配项为对应的值）
    let result = actualFormat;
    for (const [key, value] of Object.entries(formatParts)) {
      const regex = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      result = result.replace(regex, value);
    }

    return result;
  }

  /**
   * 根据身份证号获取信息
   * @param {string} idCard - 身份证号
   * @param {number} type - 获取信息类型：1-生日，2-性别，3-年龄
   * @returns {string|number} 根据type返回对应信息
   */
  function getIdCardInfo(idCard, type) {
    if (!isString(idCard) || !isNumber(type)) return '';

    // 验证身份证格式
    const reg = /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/;
    if (!reg.test(idCard)) return '';

    switch (type) {
      case 1: // 获取生日
        let birthday = '';
        if (idCard.length === 15) {
          birthday = '19' + idCard.substring(6, 8) + '-' + idCard.substring(8, 10) + '-' + idCard.substring(10, 12);
        } else if (idCard.length === 18) {
          birthday = idCard.substring(6, 10) + '-' + idCard.substring(10, 12) + '-' + idCard.substring(12, 14);
        }
        return birthday;

      case 2: // 获取性别
        let genderCode = '';
        if (idCard.length === 15) {
          genderCode = idCard.substring(14, 15);
        } else if (idCard.length === 18) {
          genderCode = idCard.substring(16, 17);
        }
        return parseInt(genderCode) % 2 === 1 ? "1" : "0"; // 1-男，0-女

      case 3: // 获取年龄
        let birthYear = '';
        if (idCard.length === 15) {
          birthYear = '19' + idCard.substring(6, 8);
        } else if (idCard.length === 18) {
          birthYear = idCard.substring(6, 10);
        }

        const ageDate = new Date();
        const month = ageDate.getMonth() + 1;
        const day = ageDate.getDate();
        let age = ageDate.getFullYear() - parseInt(birthYear) - 1;

        const birthMonth = parseInt(idCard.substring(10, 12));
        const birthDay = parseInt(idCard.substring(12, 14));

        if (birthMonth < month || (birthMonth === month && birthDay <= day)) {
          age++;
        }
        return age <= 0 ? 1 : age;

      default:
        return '';
    }
  }

  /**
   * 出生日期换算年龄
   * @param {string|Date} birth - 出生日期
   * @param {string} type - 返回类型：'yy'-周岁，'MM'-月龄，'yyMMdd'-详细
   * @param {string|Date} appointDate - 指定日期，默认为当前日期
   * @returns {number|string} 根据type返回对应年龄信息
   */
  function birthDateToYear(birth, type = 'yy', appointDate = '') {
    if (!birth) return 0;

    const birthDate = new Date(birth);
    const currentDate = appointDate ? new Date(appointDate) : new Date();

    // 计算年份差异
    let years = currentDate.getFullYear() - birthDate.getFullYear();
    // 计算月份差异
    let months = currentDate.getMonth() - birthDate.getMonth();
    // 计算天数差异
    let days = currentDate.getDate() - birthDate.getDate();

    // 如果当前日期的月份小于出生日期的月份，或者月份相同但当前日期的天数小于出生日期的天数，则调整年、月、日的差异
    if (months < 0 || (months === 0 && days < 0)) {
      years--;
      months += 12;
    }
    if (days < 0) {
      // 获取上个月的最后一天
      const lastDayOfPreviousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();
      days += lastDayOfPreviousMonth;
      months--;
    }

    switch (type) {
      case 'yy':
        return years;
      case 'MM':
        return years > 0 ? years * 12 + months : months;
      case 'yyMMdd':
        return years + '岁' + (months < 10 ? '0' + months : months) + '月' + (days < 10 ? '0' + days : days) + '日';
      default:
        return years;
    }
  }

  /**
  * 格式化数字为货币格式
  * @param {number} num - 数字
  * @param {string} currency - 货币符号(可以覆写位null/''，已处理边界)
  * @returns {string} 格式化后的货币（负号在货币前："-¥0.99"）
  */
  function formatMoney(num, currency = '¥') {
    // 确保 currency 是有效字符串
    if (!currency || typeof currency !== 'string' || currency.trim() === '') {
      currency = '';
    }

    // 输入验证
    if (num === null || num === undefined || isNaN(num) || !isFinite(num)) {
      return currency + '0.00';
    }

    // 处理负数
    const isNegative = num < 0;
    const absoluteNum = Math.abs(num);

    const formatted = currency + absoluteNum.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // 负数时将负号放在货币符号前面
    return isNegative ? '-' + formatted : formatted;
  }

  /**
   * 数字转大写汉字
   * @param {number|string} num - 要转换的数字
   * @returns {string} 大写汉字表示
   */
  function toChinesNum(num) {
    if (num == '' || num == null || num == undefined) return ''

    let changeNum = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
    let unit = ["", "拾", "佰", "仟", "万"];
    num = parseInt(num);
    let getWan = (temp) => {
      let strArr = temp.toString().split("").reverse();
      let newNum = "";
      for (var i = 0; i < strArr.length; i++) {
        newNum = (i == 0 && strArr[i] == 0 ? "" : (i > 0 && strArr[i] == 0 && strArr[i - 1] == 0 ? "" : changeNum[strArr[i]] + (strArr[i] == 0 ? unit[0] : unit[i]))) + newNum;
      }
      return newNum;
    }
    let overWan = Math.floor(num / 10000);
    let noWan = num % 10000;
    if (noWan.toString().length < 4) noWan = "0" + noWan;
    return overWan ? getWan(overWan) + "万" + getWan(noWan) : getWan(num);
  }

  /**
  * 获取URL参数
  * @param {string} url - URL地址，默认为当前页面URL，支持完整URL、相对路径、查询字符串
  * @returns {Object} 参数key-value对象
  */
  function parseQueryParams(url) {
    try {
      let searchStr = '';

      // 如果没有传入url参数，则使用当前页面URL
      if (!url || typeof url !== 'string') {
        searchStr = window.location.search;
      } else {
        // 标准化输入
        if (url.includes('://')) {
          // 完整URL
          const urlObj = new URL(url);
          searchStr = urlObj.search;
        } else if (url.startsWith('?')) {
          // 查询字符串
          searchStr = url;
        } else if (url.includes('=')) {
          // 纯参数
          searchStr = '?' + url;
        } else {
          // 尝试解析为URL
          try {
            const urlObj = new URL(url, location.href);
            searchStr = urlObj.search;
          } catch (e) {
            searchStr = '?' + url;
          }
        }
      }

      // 使用URLSearchParams解析参数
      const obj = {};
      if (searchStr) {
        const searchParams = new URLSearchParams(searchStr);
        for (const [key, value] of searchParams) {
          obj[decodeURIComponent(key)] = decodeURIComponent(value);
        }
      }

      return obj;
    } catch (error) {
      console.warn('Failed to parse URL parameters:', error);
      return {};
    }
  }

  /**
  * 序列化对象为查询字符串
  * @param {Object} obj - 要序列化的参数对象
  * @returns {string} 序列化后的查询字符串
  */
  function serializeParams(obj) {
    if (!obj || typeof obj !== 'object') {
      return '';
    }

    return Object.keys(obj)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
      .join('&');
  }

  /**
  * 获取设备类型
  * tablet ——平板电脑是一种拥有大尺寸触摸屏的移动设备, 触摸屏操作为主, ARM架构, 运行移动操作系统（iOS、Android）
  * @returns {Object} 包含设备类型和判断的对象
  */
  function getDeviceType() {
    try {
      const userAgent = navigator.userAgent;
      const width = window.innerWidth;

      // 更准确的设备检测
      const isTablet = /iPad|Android(?=.*\bTablet\b)|Tablet/i.test(userAgent) ||
        (width >= 768 && width <= 1024 && /Android|iPad/i.test(userAgent)) ||
        (Math.min(screen.width, screen.height) >= 768 && Math.max(screen.width, screen.height) <= 1366 && /iPad|Android/i.test(userAgent));

      const isMobile = /Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) && !isTablet;

      let deviceType = 'desktop';
      if (isTablet) {
        deviceType = 'tablet';
      } else if (isMobile) {
        deviceType = 'mobile';
      }

      return {
        type: deviceType,
        isMobile: deviceType === 'mobile',
        isTablet: deviceType === 'tablet',
        isDesktop: deviceType === 'desktop'
      };
    } catch (error) {
      console.warn('Failed to detect device type:', error);
      return {
        type: 'unknown',
        isMobile: false,
        isTablet: false,
        isDesktop: false
      };
    }
  }

  /**
  * 获取浏览器类型和版本[兼容pc和手机]
  * @returns {Object} 包含浏览器名称和版本的对象
  */
  function getBrowserInfo() {
    try {
      const userAgent = navigator.userAgent;
      let browserName = 'Unknown';
      let browserVersion = 'Unknown';

      // Chrome 必须在 Safari 和 Firefox 之前检测
      // 因为 Chrome userAgent 包含 Safari 和 Firefox 字符串
      if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
        browserName = 'Chrome';
        const match = userAgent.match(/Chrome\/(\d+)/);
        browserVersion = match && match[1] ? match[1] : 'Unknown';
      }
      // Edge (基于 Chromium)
      else if (userAgent.includes('Edg')) {
        browserName = 'Edge';
        const match = userAgent.match(/Edg\/(\d+)/);
        browserVersion = match && match[1] ? match[1] : 'Unknown';
      }
      // Firefox
      else if (userAgent.includes('Firefox')) {
        browserName = 'Firefox';
        const match = userAgent.match(/Firefox\/(\d+)/);
        browserVersion = match && match[1] ? match[1] : 'Unknown';
      }
      // Safari
      else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
        browserName = 'Safari';
        const match = userAgent.match(/Version\/(\d+)/);
        browserVersion = match && match[1] ? match[1] : 'Unknown';
      }
      // Internet Explorer
      else if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
        browserName = 'Internet Explorer';
        const match = userAgent.match(/(?:MSIE |rv:)(\d+)/);
        browserVersion = match && match[1] ? match[1] : 'Unknown';
      }

      return {
        name: browserName,
        version: browserVersion,
        isChrome: browserName === 'Chrome',
        isFirefox: browserName === 'Firefox',
        isSafari: browserName === 'Safari',
        isEdge: browserName === 'Edge',
        isIE: browserName === 'Internet Explorer'
      };
    } catch (error) {
      console.warn('Failed to detect browser info:', error);
      return {
        name: 'Unknown',
        version: 'Unknown',
        isChrome: false,
        isFirefox: false,
        isSafari: false,
        isEdge: false,
        isIE: false
      };
    }
  }

  /**
   * 节流函数
   * 用于限制函数的执行频率，保证在指定时间间隔内最多执行一次
   * 常用于处理高频触发但不需要每次都执行的事件【提交】
   * 
   * 工作原理：
   * - 记录上次执行的时间戳;
   * - 如果间隔大于等于设定的延迟时间，则执行目标函数并更新执行时间
   * - 如果间隔小于设定的延迟时间，则忽略此次触发
   * 
   * @param {Function} func - 要节流的目标函数
   * @param {number} delay - 节流时间间隔（毫秒）
   * @returns {Function} 返回节流处理后的函数
   */
  function throttle(func, delay) {
    let lastCall = 0;
    return function (...args) {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        func.apply(this, args);
      }
    };
  }
  /**
   * 防抖函数
   * 用于限制函数的执行频率，在指定延迟时间内如果再次触发则重新计时
   * 常用于处理用户输入、窗口调整大小、按钮点击等频繁触发的事件【窗口】
   * 
   * 工作原理：
   * - 当事件触发时，如果之前已设置定时器则清除它
   * - 重新设置定时器，在延迟时间后执行目标函数
   * - 如果在延迟时间内再次触发事件，之前的定时器会被清除，重新开始计时
   * 
   * @param {Function} func - 要防抖的目标函数
   * @param {number} delay - 延迟时间（毫秒）
   * @returns {Function} 返回防抖处理后的函数
   */
  function debounce(func, delay) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  }

  //   节流（记录时间戳比对后忽略）先执行，防抖（清除计时器，重新计时）后执行
  //   const handleScroll = throttle(function() {
  //     console.log('点击');
  //   }, 100);
  //   window.addEventListener('scroll', handleScroll);

  /**
  * 对象深度合并
  * @param {...Object} objects - 要合并的对象（可以多个）
  * @returns {Object} 合并后的对象（不修改源对象）
  */
  function deepMerge(...objects) {
    const isObject = obj => obj && typeof obj === 'object' && !Array.isArray(obj);
    const isArray = obj => Array.isArray(obj);

    return objects.reduce((prev, obj) => {
      Object.keys(obj).forEach(key => {
        const pVal = prev[key];
        const oVal = obj[key];

        if (isObject(pVal) && isObject(oVal)) {
          prev[key] = deepMerge(pVal, oVal);
        } else if (isArray(pVal) && isArray(oVal)) {
          // 数组合并而非覆盖
          prev[key] = [...pVal, ...oVal];
        } else {
          prev[key] = oVal;
        }
      });
      return prev;
    }, {});
  }

  /**
  * 深度比较两个对象是否相等
  * @param {*} obj1 - 对象1
  * @param {*} obj2 - 对象2
  * @returns {boolean} 是否相等
  */
  function deepEqual(obj1, obj2) {
    if (obj1 === obj2) return true;

    if (obj1 == null || obj2 == null) return obj1 === obj2;

    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return obj1 === obj2;

    if (obj1.constructor !== obj2.constructor) {
      // 如果一个是普通对象，另一个是继承对象，但仍具有相同的属性，则认为相等
      if (!(obj1 instanceof Object) || !(obj2 instanceof Object)) {
        return false;
      }
    }

    if (obj1 instanceof Date && obj2 instanceof Date) {
      return obj1.getTime() === obj2.getTime();
    }

    if (obj1 instanceof RegExp && obj2 instanceof RegExp) {
      return obj1.toString() === obj2.toString();
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
  }

  /**
  * 获取对象深层属性值
  * @param {Object} obj - 源对象
  * @param {string} path - 属性路径，如 'a.b.c'
  * 属性路径，支持 'users[0].name' 或 'users.0.name' 格式对象链式取值
  * Utils.deepGet(obj,'children[0].children[0].children[0].label') // 多层对象数组嵌套结构
  * @param {*} defaultValue - 默认值
  * @returns {*} 属性值或默认值
  */
  function deepGet(obj, path, defaultValue = undefined) {
    if (!obj || typeof obj !== 'object') {
      return defaultValue;
    }

    // 支持两种格式: users[0].name 或 users.0.name
    const keys = path.replace(/\[(\w+)\]/g, '.$1').split('.');
    let result = obj;

    for (const key of keys) {
      if (result == null || typeof result !== 'object') {
        return defaultValue;
      }

      result = result[key];
    }

    return result !== undefined ? result : defaultValue;
  }

  /**
  * 平行数组转树结构
  * @param {Object} arr - 源对象
  * @param {string} id/pId - 关系字段属性名
  * @returns {*} 最后一级集合
  */
  function tranListToTreeData(arr, id = 'id', pId = 'pId') {
    const newArr = []
    const map = {}
    arr.forEach(item => {
      item.children = []
      const key = item[id]
      map[key] = item
    })
    arr.forEach(item => {
      const parent = map[item[pId]]
      if (parent) {
        parent.children.push(item)
      } else {
        newArr.push(item)
      }
    })
    return newArr
  }

  /**
  * 寻找树形数据最后一级元素
  * @param {Object} obj - 源对象
  * @returns {*} 最后一级集合
  */
  function getTreeLastLevel(tree) {
    const result = [];
    function traverse(node) {
      if (!node.children || node.children.length === 0) {
        // 如果节点没有子节点，则将其添加到结果数组中
        result.push(node);
      } else {
        // 否则，遍历每个子节点
        node.children.forEach(child => {
          traverse(child);
        });
      }
    }
    // 遍历树的根节点
    tree.forEach(rootNode => {
      traverse(rootNode);
    });

    return result;
  }

  // 暴露公共方法
  return {
    // 判断类
    isNull, // 判空
    isArray,
    isObject,
    isFunction,
    isString,
    isNumber,
    isUrl,
    isWeixin, // 微信内置浏览器

    // 验证类--Reg
    validCreditCode,
    validPassWord,
    validEmail,
    validPhone, // 验证联系方式（座机+手机）
    validMobile,
    validCardNo, // 验证证件号码
    validDate, // 判断是否为可解析的日期

    // 通用方法
    uniqueArray, // 数组去重
    deepClone,
    copyText, // 复制文本
    sleep, // 休眠（Promise）
    maskedStr, // 掩码字符串（脱敏）

    // 随机数
    getRandom, // 获取随机数(0,100)
    getUuid, // 获取uuid（36位）

    // 日期/时间处理
    getYearCurr, // 获取当前年份
    getYearRange, // 获取指定时间范围
    getDatePicker, // 日期格式处理
    getIdCardInfo, // 身份证获取性别生日
    birthDateToYear, // 获取至今的年龄月龄

    // 金额处理
    formatMoney, // 金额格式化(千位加逗号)
    toChinesNum, // 数字转大写汉字

    // 系列化-反序列化
    parseQueryParams, // 获取url参数（不传获取当前url, 支持完整URL、相对路径、查询字符串）
    serializeParams, // 参数序列化(同parseQueryParams相反)

    // 设备信息
    getDeviceType,// 获取设备类型 tablet/mobile/desktop
    getBrowserInfo, // 获取浏览器信息(Chrome/Firefox/Safari/Edge/Internet Explorer)

    // 节流防抖
    throttle, // 节流函数（点击防重）————节流会先执行一次, 防抖不会
    debounce, // 防抖函数（窗口滚动）

    // 深层次对象操作
    deepMerge, // 深度合并对象（返回合并后对象，不修改原对象）
    deepEqual, // 深度比较对象是否相等
    deepGet, // 获取对象深层次属性值

    // 树形结构处理
    tranListToTreeData,
    getTreeLastLevel,

  };
}));

/**
* CommonJS (Node.js 环境)
* const tools = require('./global-utils.js');
* console.log(tools.isNull(null)); // true
*/

/**
* AMD (异步模块定义)
* define(['./global-utils.js'], function(tools) {
*   console.log(tools.isNull(undefined)); // true
});
*/

/**
* 浏览器全局变量————自执行函数已导出工具函数
* <script src="./global-utils.js"></script>
* console.log(tools.isNull('')); // true
*/

/**
* ES6 模块 (通过构建工具)
* import tools from './global-utils.js';
* console.log(tools.isNull({})); // false
*
* // 将 tools 挂载到 Vue 原型上
* Vue.prototype.$tools = tools
*/